import React, {
  useMemo,
} from "react";

import {
  getShiftStartMs,
  getShiftEndMs,
} from "../../../core/shifts/shiftLifecycle";

import {
  getShiftFinancialSnapshot,
} from "../../../core/shifts/shiftFinancials";

import {
  getShiftOperationalSummary,
} from "../../../core/shifts/shiftOperationalSummary";
import Shift360StaffingSnapshot
  from "./Shift360StaffingSnapshot";

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate ===
      "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(
      parsed.getTime()
    )
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDate(value) {
  const parsed =
    toDate(value);

  return parsed
    ? parsed.toLocaleDateString(
        [],
        {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        }
      )
    : "Date unavailable";
}

function formatTime(value) {
  const parsed =
    toDate(value);

  return parsed
    ? parsed.toLocaleTimeString(
        [],
        {
          hour: "numeric",
          minute: "2-digit",
        }
      )
    : "—";
}

function formatDateTime(
  value
) {
  const parsed =
    toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Not recorded";
}

function titleCase(value) {
  return String(
    value || "unknown"
  )
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
}

function formatMoney(value) {
  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  );
}

function getStatusTone(
  status
) {
  if (
    status ===
    "completed"
  ) {
    return {
      color: "#166534",
      background: "#dcfce7",
      border: "#bbf7d0",
    };
  }

  if (
    [
      "assigned",
      "activated",
      "tracking",
      "arrived",
    ].includes(status)
  ) {
    return {
      color: "#1d4ed8",
      background: "#dbeafe",
      border: "#bfdbfe",
    };
  }

  if (
    [
      "no_show",
      "cancelled",
      "canceled",
    ].includes(status)
  ) {
    return {
      color: "#b91c1c",
      background: "#fee2e2",
      border: "#fecaca",
    };
  }

  if (
    status ===
    "closed_unfilled"
  ) {
    return {
      color: "#475569",
      background: "#f1f5f9",
      border: "#cbd5e1",
    };
  }

  return {
    color: "#92400e",
    background: "#fef3c7",
    border: "#fde68a",
  };
}

function getOperationalTone(
  state
) {
  const value =
    String(
      state || ""
    ).toLowerCase();

  if (
    [
      "accepted",
      "arrived",
      "completed",
      "time confirmed",
    ].includes(value)
  ) {
    return "#166534";
  }

  if (
    [
      "assigned",
      "activated",
      "on my way",
    ].includes(value)
  ) {
    return "#1d4ed8";
  }

  if (
    [
      "declined",
      "no show",
      "cancelled",
    ].includes(value)
  ) {
    return "#b91c1c";
  }

  if (
    [
      "pending",
      "unassigned",
      "time missing",
    ].includes(value)
  ) {
    return "#b45309";
  }

  return "#475569";
}

function MetricCard({
  label,
  value,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 13,
        border:
          "1px solid #e2e8f0",
        background:
          "#ffffff",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 9,
          fontWeight: 900,
          textTransform:
            "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 5,
          color,
          fontSize: 17,
          fontWeight: 950,
          overflowWrap:
            "anywhere",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function OperationalRow({
  label,
  value,
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent:
          "space-between",
        alignItems:
          "center",
        gap: 12,
        padding:
          "8px 0",
        borderBottom:
          "1px solid #e2e8f0",
      }}
    >
      <span
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 800,
        }}
      >
        {label}
      </span>

      <span
        style={{
          color:
            getOperationalTone(
              value
            ),
          fontSize: 10,
          fontWeight: 950,
          textAlign:
            "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default function Shift360Overview({
  shift,
  worker,
  facility,
  eligibilitySummary = null,
  showFinancials = false,
}) {
  const summary =
    useMemo(() => {
      if (!shift) {
        return null;
      }

      const operational =
        getShiftOperationalSummary(
          shift
        );

      const financials =
        getShiftFinancialSnapshot(
          shift
        );

      return {
        ...operational,
        ...financials,
      };
    }, [shift]);

  if (
    !shift ||
    !summary
  ) {
    return null;
  }

  const statusTone =
    getStatusTone(
      summary.status
    );

  const workerName =
    worker?.displayName ||
    worker?.name ||
    worker?.fullName ||
    shift?.assignedWorkerName ||
    shift?.workerName ||
    "Unassigned";

  const facilityName =
    facility?.facilityName ||
    facility?.name ||
    shift?.facilityName ||
    shift?.facilityId ||
    "Facility";

  const role =
    shift?.role ||
    shift?.position ||
    shift?.requiredPosition ||
    "Shift";

  const lifecycleRows = [
    [
      "Created",
      shift?.createdAt,
    ],

    [
      "Offered",
      shift?.offeredAt,
    ],

    [
      "Requested",
      shift?.requestedAt,
    ],

    [
      "Assigned",
      shift?.assignedAt,
    ],

    [
      "Accepted",
      shift?.acceptedAt,
    ],

    [
      "Activated",
      shift?.activatedAt,
    ],

    [
      "On My Way",
      shift?.trackingStartedAt ||
        shift?.trackingAt ||
        shift?.onMyWayAt,
    ],

    [
      "Arrived",
      shift?.arrivedAt ||
        shift?.arrivalConfirmedAt,
    ],

    [
      "Completed",
      shift?.completedAt,
    ],

    ...(summary.status ===
      "no_show" ||
    shift?.noShowAt
      ? [
          [
            "No Show",
            shift?.noShowAt,
          ],
        ]
      : []),

    ...([
      "cancelled",
      "canceled",
    ].includes(
      summary.status
    ) ||
    shift?.cancelledAt ||
    shift?.canceledAt
      ? [
          [
            "Cancelled",
            shift?.cancelledAt ||
              shift?.canceledAt,
          ],
        ]
      : []),

    ...(summary.status ===
      "closed_unfilled" ||
    shift?.closedUnfilledAt ||
    shift?.closedAt
      ? [
          [
            "Closed Unfilled",
            shift?.closedUnfilledAt ||
              shift?.closedAt,
          ],
        ]
      : []),
  ];

  return (
    <div
      style={{
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          padding: 15,
          borderRadius: 16,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #172554 0%, #2563eb 100%)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                color: "#bfdbfe",
                fontSize: 9,
                fontWeight: 900,
                textTransform:
                  "uppercase",
              }}
            >
              Shift 360
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 22,
                fontWeight: 950,
              }}
            >
              {String(role)}
            </div>

            <div
              style={{
                marginTop: 4,
                color: "#dbeafe",
                fontSize: 11,
                fontWeight: 750,
              }}
            >
              {String(
                facilityName
              )}
            </div>
          </div>

          <span
            style={{
              padding:
                "6px 9px",
              borderRadius: 999,
              border:
                `1px solid ${statusTone.border}`,
              color:
                statusTone.color,
              background:
                statusTone.background,
              fontSize: 9,
              fontWeight: 950,
              textTransform:
                "uppercase",
              whiteSpace:
                "nowrap",
            }}
          >
            {summary.statusLabel ||
              titleCase(
                summary.status
              )}
          </span>
        </div>

        <div
          style={{
            marginTop: 13,
            color: "#ffffff",
            fontSize: 12,
            fontWeight: 850,
          }}
        >
          {formatDate(
            getShiftStartMs(
              shift
            )
          )}
        </div>

        <div
          style={{
            marginTop: 3,
            color: "#dbeafe",
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          {formatTime(
            getShiftStartMs(
              shift
            )
          )}

          {" – "}

          {formatTime(
            getShiftEndMs(
              shift
            )
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <MetricCard
          label="Hours"
          value={
            summary.hours.toFixed(
              1
            )
          }
          color="#7c3aed"
        />

        <MetricCard
          label="Coverage Type"
          value={titleCase(
            summary.coverageType
          )}
          color="#1d4ed8"
        />

        <MetricCard
          label="Assigned Worker"
          value={workerName}
          color={
            summary.assigned
              ? "#166534"
              : "#b45309"
          }
        />

        <MetricCard
          label="Facility"
          value={facilityName}
        />
      </div>

      <div
        style={{
          padding: 13,
          borderRadius: 14,
          border:
            "1px solid #dbeafe",
          background:
            "#eff6ff",
        }}
      >
        <div
          style={{
            color: "#1e3a8a",
            fontSize: 13,
            fontWeight: 950,
          }}
        >
          Operational Snapshot
        </div>

        <div
          style={{
            marginTop: 6,
          }}
        >
          <OperationalRow
            label="Assignment"
            value={
              summary.assignmentLabel
            }
          />

          <OperationalRow
            label="Worker Response"
            value={
              summary.responseLabel
            }
          />

          <OperationalRow
            label="Movement"
            value={
              summary.movementLabel
            }
          />

          <OperationalRow
            label="Shift Time"
            value={
              summary.timeLabel
            }
          />

          <OperationalRow
            label="Lifecycle"
            value={
              summary.terminal
                ? "Terminal"
                : "Active"
            }
          />

          <Shift360StaffingSnapshot
            worker={worker}
            eligibilitySummary={
              eligibilitySummary
            }
          />

        </div>
      </div>

      <div
        style={{
          padding: 13,
          borderRadius: 14,
          border:
            "1px solid #e2e8f0",
          background:
            "#f8fafc",
        }}
      >
        <div
          style={{
            color: "#0f172a",
            fontSize: 13,
            fontWeight: 950,
          }}
        >
          Lifecycle Summary
        </div>

        <div
          style={{
            display: "grid",
            gap: 7,
            marginTop: 10,
          }}
        >
          {lifecycleRows.map(
            ([label, value]) => (
              <div
                key={label}
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  gap: 10,
                  paddingBottom: 6,
                  borderBottom:
                    "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    color:
                      "#475569",
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {label}
                </span>

                <span
                  style={{
                    color: value
                      ? "#0f172a"
                      : "#94a3b8",
                    fontSize: 10,
                    fontWeight: 800,
                    textAlign:
                      "right",
                  }}
                >
                  {value
                    ? formatDateTime(
                        value
                      )
                    : "Not recorded"}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      {showFinancials ? (
        <div
          style={{
            padding: 13,
            borderRadius: 14,
            border:
              "1px solid #ddd6fe",
            background:
              "#faf5ff",
          }}
        >
          <div
            style={{
              color: "#6d28d9",
              fontSize: 13,
              fontWeight: 950,
            }}
          >
            Financial Snapshot
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",
              gap: 8,
              marginTop: 10,
            }}
          >
            <MetricCard
              label="Bill Rate"
              value={formatMoney(
                summary.billRate
              )}
            />

            <MetricCard
              label="Pay Rate"
              value={formatMoney(
                summary.payRate
              )}
            />

            <MetricCard
              label="Invoice"
              value={formatMoney(
                summary.invoice
              )}
              color="#166534"
            />

            <MetricCard
              label="Payroll"
              value={formatMoney(
                summary.payroll
              )}
              color="#1d4ed8"
            />

            <MetricCard
              label="Gross Margin"
              value={formatMoney(
                summary.margin
              )}
              color={
                summary.margin >= 0
                  ? "#7c3aed"
                  : "#b91c1c"
              }
            />

            <MetricCard
              label="Margin %"
              value={`${Number(
                summary.marginPercent ||
                  0
              ).toFixed(1)}%`}
              color={
                summary.margin >= 0
                  ? "#7c3aed"
                  : "#b91c1c"
              }
            />
          </div>
        </div>
      ) : null}

      <div
        style={{
          color: "#64748b",
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        Shift ID:{" "}
        {String(
          shift?.id ||
            shift?.shiftId ||
            "Unavailable"
        )}
      </div>
    </div>
  );
}